import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";

import { axiosGetRequest, axiosPutRequest } from "../api/ApiClient";
import AssociationComponent from "../components/AssociationComponent";
import Autocomplete from "../components/Autocomplete";
import DialogMessage from "../components/DialogMessage";
import Professor from "../model/dto/Professor";
import {
    StyledBody,
    StyledHeader,
    StyledLabel,
    StyledSubTitle,
    StyledTitle,
} from "../styles/MainStyles";
import GenericUtils from "../utils/GenericUtils";
import StringUtils from "../utils/types/StringUtils";

function CoordinatingAssociation() {
    const theme = {};

    const { instance } = useMsal();
    const acessTokenDocente = GenericUtils.getAccessTokenDocente();

    const translate = {
        operationSuccess: StringUtils.translate("operationSuccess"),
        operationFailure: StringUtils.translate("operationFailure"),
    };

    const [, setAddRemove] = useState("");
    const [selectTeachers, setSelectTeachers] = useState("");
    const [coordinations, setCoordinations] = useState<any[]>([]);
    const [teachers, setTeachers] = useState<any[]>([]);
    const [getTeachers, setGetTeachers] = useState<any[]>([]);
    const [teachersSeletable, setTeachersSeletable] = useState<any[]>([]);
    const [selected, setSelected] = useState<any[]>([]);
    const [isSave, setIsSave] = useState(false);

    // Dialog
    const [showDialogSuccess, setShowDialogSuccess] = useState(false);
    const [showDialogError, setShowDialogError] = useState(false);

    async function getActiveTeachers() {
        const response = await axiosGetRequest(
            "/docente/v1/professores?_limit=99999&ativos=true&rescisao=false",
            acessTokenDocente,
            instance
        );
        setGetTeachers(response.data);
        setTeachers(
            response.data.map((data: any) => {
                return {
                    value: data.matriculaAdp,
                    label: data.nome,
                };
            })
        );
    }

    async function getCoordinations() {
        const selectTeacherId = getTeachers.find(
            (teacher) => teacher.matriculaAdp === selectTeachers
        );
        const response = await axiosGetRequest(
            `/docente/v1/professores/${selectTeacherId?.docenteID}/centros-resultado/${selectTeacherId?.cr}/coordenadores`,
            acessTokenDocente,
            instance
        );
        setCoordinations(response.data);
    }

    async function putLinkedTeachers(params: Professor[]) {
        const selectTeacherId = getTeachers.find(
            (teacher) => teacher.matriculaAdp === selectTeachers
        );

        const adicionados = params.filter(
            (item: any) =>
                !coordinations.some(
                    (item2: any) => item.matriculaAdp === item2.matriculaAdp
                )
        );

        const removidos = coordinations.filter(
            (item: any) =>
                !params.some(
                    (item2: any) => item.matriculaAdp === item2.matriculaAdp
                )
        );

        const payload = {
            adicionados: adicionados.map((item) => item.docenteID),
            removidos: removidos.map((item) => item.docenteID),
        };

        const response = await axiosPutRequest(
            `/docente/v1/professores/${selectTeacherId.docenteID}/vinculo-coordenadores`,
            payload,
            acessTokenDocente,
            instance
        );

        if (GenericUtils.isSuccessStatus(response.status)) {
            setShowDialogSuccess(true);
            setIsSave(false);
            getCoordinations();
        } else {
            setShowDialogError(true);
        }
    }

    const handlePutLinkedTeachers = (params: Professor[]) => {
        const coordinationsParams = {
            coordenares: params.filter(
                (element) => element.coordenadorVinculo !== "N"
            ),
        };

        putLinkedTeachers(coordinationsParams.coordenares);
    };

    const handleCloseDialogSuccess = () => {
        setShowDialogSuccess(false);
    };

    const handleCloseDialogError = () => {
        setShowDialogError(false);
    };

    useEffect(() => {
        getActiveTeachers();
    }, []);

    useEffect(() => {
        if (selected.length > 0) {
            setSelectTeachers(selected[0].value);
            setTeachersSeletable(
                getTeachers.filter(
                    (item) => item.matriculaAdp !== selected[0].value
                )
            );
        } else {
            setSelectTeachers("");
        }
    }, [selected]);

    useEffect(() => {
        if (selectTeachers) {
            getCoordinations();
        }
    }, [selectTeachers]);

    const selectActivity =
        selectTeachers !== "" ? (
            <>
                <StyledLabel>
                    2. Selecione o coordenador que deseja associar
                </StyledLabel>
                <AssociationComponent
                    association={teachersSeletable}
                    linked={coordinations}
                    title="Coordenador disponível"
                    titleSecond="Coordenador atribuído"
                    onClick={handlePutLinkedTeachers}
                    setAddRemove={setAddRemove}
                    isSave={isSave}
                    setIsSave={setIsSave}
                    validLinked={selectTeachers}
                />
            </>
        ) : null;

    const DialogSuccess = (
        <DialogMessage
            type="success"
            show={showDialogSuccess}
            title={translate.operationSuccess}
            onClose={handleCloseDialogSuccess}
        />
    );

    const DialogError = (
        <DialogMessage
            type="error"
            show={showDialogError}
            title={translate.operationFailure}
            onClose={handleCloseDialogError}
        />
    );

    return (
        <ThemeProvider theme={theme}>
            <StyledHeader>
                <StyledTitle>Associação de Coordenador</StyledTitle>
                <StyledSubTitle>
                    Use esse espaço para selecionar os coordenadores atribuídos
                    a cada professor.
                </StyledSubTitle>
            </StyledHeader>
            <StyledBody>
                <StyledLabel>1. Selecione o professor </StyledLabel>
                <div style={{ display: "flex" }}>
                    <Autocomplete
                        onChange={setSelected}
                        options={teachers}
                        placeholder="Professor"
                        selected={selected}
                    />
                </div>
                {selectActivity}
            </StyledBody>
            {showDialogSuccess ? DialogSuccess : null}
            {showDialogError ? DialogError : null}
        </ThemeProvider>
    );
}

export default CoordinatingAssociation;
